import { dictionaryApi } from './dictionaryClient';

export type Shivasutra = {
  id: number;
  sutraOrder: number;
  sutra: string;
  kashika: string;
  vyakhya: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateShivasutraBody = {
  sutraOrder: number;
  sutra: string;
  kashika: string;
  vyakhya: string;
};

export type UpdateShivasutraBody = Partial<CreateShivasutraBody>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function num(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return undefined;
}

function str(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

function pickStr(o: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(o, k)) return str(o[k]);
  }
  return '';
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw && isRecord(raw.data) && !Array.isArray(raw.data)) {
    return raw.data;
  }
  return raw;
}

/** Maps API rows (camelCase or snake_case) to the client shape. */
export function normalizeShivasutra(raw: unknown): Shivasutra | null {
  if (!isRecord(raw)) return null;
  const id = num(raw.id);
  const sutraOrder = num(raw.sutraOrder ?? raw.sutra_order);
  if (id == null || sutraOrder == null) return null;
  return {
    id,
    sutraOrder,
    sutra: pickStr(raw, ['sutra', 'sutra_text', 'sutraText', 'text']),
    kashika: pickStr(raw, ['kashika', 'kashika_text', 'kashikaText']),
    vyakhya: pickStr(raw, ['vyakhya', 'vyakhya_text', 'vyakhyaText', 'comment']),
    createdAt: str(raw.createdAt ?? raw.created_at) || undefined,
    updatedAt: str(raw.updatedAt ?? raw.updated_at) || undefined,
  };
}

function normalizeList(data: unknown): Shivasutra[] {
  let rows: unknown[] = [];
  if (Array.isArray(data)) rows = data;
  else if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    rows = (data as { data: unknown[] }).data;
  }
  return rows.map(normalizeShivasutra).filter((x): x is Shivasutra => x != null);
}

export async function listShivasutras(): Promise<Shivasutra[]> {
  const res = await dictionaryApi.get<unknown>('/shivasutra');
  return normalizeList(res.data);
}

export async function getShivasutra(id: number): Promise<Shivasutra> {
  const res = await dictionaryApi.get<unknown>(`/shivasutra/${id}`);
  const row = normalizeShivasutra(unwrapEntityPayload(res.data));
  if (!row) {
    throw new Error('Invalid shivasutra response');
  }
  return row;
}

export async function createShivasutra(body: CreateShivasutraBody): Promise<Shivasutra> {
  const res = await dictionaryApi.post<unknown>('/shivasutra', body);
  const row = normalizeShivasutra(unwrapEntityPayload(res.data));
  if (!row) {
    throw new Error('Invalid shivasutra response');
  }
  return row;
}

export async function updateShivasutra(id: number, body: UpdateShivasutraBody): Promise<Shivasutra> {
  const res = await dictionaryApi.patch<unknown>(`/shivasutra/${id}`, body);
  const row = normalizeShivasutra(unwrapEntityPayload(res.data));
  if (!row) {
    throw new Error('Invalid shivasutra response');
  }
  return row;
}

export async function deleteShivasutra(id: number): Promise<void> {
  await dictionaryApi.delete(`/shivasutra/${id}`);
}
